import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useQueryParams } from "@trailhead/smart-setup-core";
import { FlexColumnContainer } from "@trailhead/core-ui";
import { useDeviceState } from "@trailhead/global-device-context";

import { Button as AsurionButton, Text } from "@trailhead/asurion-ui";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { DeviceSelectors } from "./DeviceSelectors";
import { GuideTypes } from "@trailhead/contentful";

const CtaSliderContainer = styled(FlexColumnContainer)`
  margin-top: 26px;
`;

const CopyContainer = styled(FlexColumnContainer)`
  align-content: space-evenly;
  margin-bottom: 38px;
`;

const Divider = styled.div`
  border-bottom: 1px solid #e6e6eb;
`;

const CopyTitle = styled(Text)`
  line-height: 150%;
  margin-bottom: 12px;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  width: 100%;
  height: 45px;
  padding: 12px 18px;
  margin-top: 16px;
`;

const Copy = styled(Text)`
  line-height: 120%;
`;

export const CTASlider: React.FC = () => {
  const t = useSanitizedTranslation();
  const isNextStepOverrideEnabled = true;

  const {
    oldDeviceFunctional,
    oldDeviceAccess,
    newDeviceObName,
    oldDeviceObName,
    newDeviceActivated,
  } = useDeviceState();

  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const skipToTutorial = queryParams.get("skipToTutorial");

  const onContinueClick = () => {
    trackEvent("Intro_Page_Completed_Click", {
      oldDeviceFunctional,
      oldDeviceAccess,
      oldDeviceObName,
      newDeviceObName,
      newDeviceActivated,
    });
    updateStickyProps({
      oldDeviceFunctional,
      oldDeviceAccess,
      oldDeviceObName,
      newDeviceObName,
      newDeviceActivated,
    });

    const goTo = Object.values(GuideTypes).includes(
      skipToTutorial as GuideTypes,
    )
      ? `/mobile-device-setup/tutorials/${skipToTutorial as string}`
      : "/mobile-device-setup/tutorials";
    navigate(goTo);
  };

  const continueButtonText = t("Continue");

  const showContinueButton = () => {
    if (
      (newDeviceActivated !== null || !isNextStepOverrideEnabled) &&
      (oldDeviceAccess === false || oldDeviceFunctional !== null)
    ) {
      return (
        <AsurionButtonWrapper
          disabled={!newDeviceObName || !oldDeviceObName}
          onClick={onContinueClick}
          size="medium"
          variant="default"
        >
          {continueButtonText}
        </AsurionButtonWrapper>
      );
    }
    return undefined;
  };

  useEffect(() => {
    trackEvent("Page_Loaded", { pageName: "Intro_Page" });
  }, []);

  return (
    <CtaSliderContainer>
      <CopyContainer>
        <CopyTitle size={3} weight={"heavy"}>
          {t("Welcome to device set up")}
        </CopyTitle>
        <Copy size={5} weight={"feather"}>
          {t("The easiest way to set up your device")}
        </Copy>
      </CopyContainer>
      <Divider />
      <DeviceSelectors />
      {showContinueButton()}
    </CtaSliderContainer>
  );
};
